<script setup lang="ts">
import { ref, computed } from "vue";
import { useTranslation } from "i18next-vue";
import type { PartsTreeNode } from "@/types/field-observations.types";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";

const props = defineProps<{
  modelValue: PartsTreeNode[];
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: PartsTreeNode[]): void;
}>();

const { t } = useTranslation();

// Expanded nodes tracking (using path as key)
const expandedPaths = ref<Set<string>>(new Set());

// Flatten tree for rendering
interface FlatNode {
  node: PartsTreeNode;
  path: string;
  level: number;
}

const flattenNodes = (
  nodes: PartsTreeNode[],
  parentPath: string,
  level: number
): FlatNode[] => {
  const result: FlatNode[] = [];

  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];
    const path = parentPath ? `${parentPath}.${i}` : `${i}`;
    result.push({ node, path, level });

    if (node.children?.length && expandedPaths.value.has(path)) {
      result.push(...flattenNodes(node.children, path, level + 1));
    }
  }

  return result;
};

const flatNodes = computed(() => {
  return flattenNodes(props.modelValue, "", 0);
});

const hasChildren = (node: PartsTreeNode): boolean => {
  return node.children?.length > 0;
};

const toggleExpand = (path: string) => {
  if (expandedPaths.value.has(path)) {
    expandedPaths.value.delete(path);
  } else {
    expandedPaths.value.add(path);
  }
  expandedPaths.value = new Set(expandedPaths.value);
};

// Update tree immutably
const updateTree = (
  nodes: PartsTreeNode[],
  path: string,
  updater: (node: PartsTreeNode) => PartsTreeNode | null
): PartsTreeNode[] => {
  const parts = path.split(".").map(Number);

  const updateAtPath = (
    current: PartsTreeNode[],
    pathParts: number[]
  ): PartsTreeNode[] => {
    if (pathParts.length === 0) return current;

    const [index, ...rest] = pathParts;
    const newArray = [...current];

    if (rest.length === 0) {
      // We're at the target
      const result = updater(newArray[index]);
      if (result === null) {
        // Delete node
        newArray.splice(index, 1);
      } else {
        newArray[index] = result;
      }
    } else {
      // Recurse into children
      newArray[index] = {
        ...newArray[index],
        children: updateAtPath(newArray[index].children, rest),
      };
    }

    return newArray;
  };

  return updateAtPath(nodes, parts);
};

// Add child to node
const addChild = (
  nodes: PartsTreeNode[],
  parentPath: string | null,
  newNode: PartsTreeNode
): PartsTreeNode[] => {
  if (parentPath === null) {
    // Add to root
    return [...nodes, newNode];
  }

  return updateTree(nodes, parentPath, (node) => ({
    ...node,
    children: [...node.children, newNode],
  }));
};

// Edit modal
const showEditModal = ref(false);
const editingPath = ref<string | null>(null);
const editingName = ref("");
const editInputRef =
  ref<InstanceType<typeof import("element-plus")["ElInput"]>>();

const startEdit = (path: string, currentName: string) => {
  editingPath.value = path;
  editingName.value = currentName;
  showEditModal.value = true;
};

const saveEdit = () => {
  if (!editingPath.value || !editingName.value.trim()) return;

  const newTree = updateTree(props.modelValue, editingPath.value, (node) => ({
    ...node,
    name: editingName.value.trim(),
  }));
  emit("update:modelValue", newTree);
  showEditModal.value = false;
  editingPath.value = null;
  editingName.value = "";
};

// Add modal
const showAddModal = ref(false);
const addParentPath = ref<string | null>(null);
const newNodeName = ref("");
const addInputRef =
  ref<InstanceType<typeof import("element-plus")["ElInput"]>>();

const openAddModal = (parentPath: string | null = null) => {
  addParentPath.value = parentPath;
  newNodeName.value = "";
  showAddModal.value = true;
};

const handleAdd = () => {
  if (!newNodeName.value.trim()) return;

  const newNode: PartsTreeNode = {
    name: newNodeName.value.trim(),
    children: [],
  };

  const newTree = addChild(props.modelValue, addParentPath.value, newNode);
  emit("update:modelValue", newTree);

  // Auto-expand parent
  if (addParentPath.value) {
    expandedPaths.value.add(addParentPath.value);
    expandedPaths.value = new Set(expandedPaths.value);
  }

  showAddModal.value = false;
  newNodeName.value = "";
  addParentPath.value = null;
};

// Delete node
const deleteNode = (path: string) => {
  const newTree = updateTree(props.modelValue, path, () => null);
  emit("update:modelValue", newTree);
};

// Deep clone a node (with all children)
const cloneNode = (node: PartsTreeNode): PartsTreeNode => {
  return {
    name: node.name,
    children: node.children.map(cloneNode),
  };
};

// Duplicate node (insert copy right after the original at the same level)
const duplicateNode = (path: string) => {
  const parts = path.split(".").map(Number);
  const index = parts[parts.length - 1];
  const parentPath = parts.length > 1 ? parts.slice(0, -1).join(".") : null;

  // Find the node to clone
  let sourceNode: PartsTreeNode | null = null;
  const findNode = (nodes: PartsTreeNode[], pathParts: number[]): void => {
    if (pathParts.length === 1) {
      sourceNode = nodes[pathParts[0]];
      return;
    }
    findNode(nodes[pathParts[0]].children, pathParts.slice(1));
  };
  findNode(props.modelValue, parts);

  if (!sourceNode) return;
  const clone = cloneNode(sourceNode);

  // Insert clone after the original
  let newTree: PartsTreeNode[];
  if (parentPath === null) {
    newTree = [...props.modelValue];
    newTree.splice(index + 1, 0, clone);
  } else {
    newTree = updateTree(props.modelValue, parentPath, (parent) => {
      const newChildren = [...parent.children];
      newChildren.splice(index + 1, 0, clone);
      return { ...parent, children: newChildren };
    });
  }

  emit("update:modelValue", newTree);
};

// Multiply modal
const showMultiplyModal = ref(false);
const multiplyPath = ref<string | null>(null);
const multiplySourceName = ref("");
const multiplyNames = ref<string[]>([]);

const generateDefaultName = (baseName: string, index: number): string => {
  // Strip trailing number/whitespace from base name: "Pile 1" -> "Pile"
  const stripped = baseName.replace(/\s*\d+\s*$/, "").trim();
  return `${stripped} ${index}`;
};

const openMultiplyModal = (path: string, name: string) => {
  multiplyPath.value = path;
  multiplySourceName.value = name;
  // Start with 3 copies by default, numbered from 1
  multiplyNames.value = [1, 2, 3].map((i) => generateDefaultName(name, i));
  showMultiplyModal.value = true;
};

const addMultiplyCopy = () => {
  const nextIndex = multiplyNames.value.length + 1;
  multiplyNames.value.push(
    generateDefaultName(multiplySourceName.value, nextIndex)
  );
};

const removeMultiplyCopy = () => {
  if (multiplyNames.value.length > 2) {
    multiplyNames.value.pop();
  }
};

const handleMultiply = () => {
  if (!multiplyPath.value) return;

  const parts = multiplyPath.value.split(".").map(Number);
  const index = parts[parts.length - 1];
  const parentPath = parts.length > 1 ? parts.slice(0, -1).join(".") : null;

  // Find source node to clone children structure
  let sourceNode: PartsTreeNode | null = null;
  const findNode = (nodes: PartsTreeNode[], pathParts: number[]): void => {
    if (pathParts.length === 1) {
      sourceNode = nodes[pathParts[0]];
      return;
    }
    findNode(nodes[pathParts[0]].children, pathParts.slice(1));
  };
  findNode(props.modelValue, parts);

  if (!sourceNode) return;

  // Create copies with custom names but same children structure
  const copies: PartsTreeNode[] = multiplyNames.value
    .filter((name) => name.trim())
    .map((name) => ({
      name: name.trim(),
      children: sourceNode!.children.map(cloneNode),
    }));

  // Replace the original node with all the copies
  let newTree: PartsTreeNode[];
  if (parentPath === null) {
    newTree = [...props.modelValue];
    newTree.splice(index, 1, ...copies);
  } else {
    newTree = updateTree(props.modelValue, parentPath, (parent) => {
      const newChildren = [...parent.children];
      newChildren.splice(index, 1, ...copies);
      return { ...parent, children: newChildren };
    });
  }

  emit("update:modelValue", newTree);
  showMultiplyModal.value = false;
};
</script>

<template>
  <div class="structure-editor">
    <!-- Empty state -->
    <div v-if="!modelValue || modelValue.length === 0" class="empty-state">
      <PhosphorIcon name="tree-structure" :size="48" class="empty-icon" />
      <p>{{ t("dashboard:fieldObservations.partTemplates.editor.empty") }}</p>
    </div>

    <!-- Tree -->
    <div v-else class="tree">
      <div
        v-for="{ node, path, level } in flatNodes"
        :key="path"
        class="tree-item"
        :style="{ paddingLeft: level * 24 + 12 + 'px' }"
      >
        <!-- Expand button -->
        <button
          v-if="hasChildren(node)"
          type="button"
          class="btn-expand"
          @click="toggleExpand(path)"
        >
          <PhosphorIcon
            name="caret-right"
            :size="16"
            class="expand-icon"
            :class="{ expanded: expandedPaths.has(path) }"
          />
        </button>
        <span v-else class="expand-spacer" />

        <!-- Node name -->
        <span class="node-name">{{ node.name }}</span>

        <!-- Actions -->
        <div class="node-actions">
          <el-dropdown
            trigger="click"
            @command="
              (cmd: string) => {
                if (cmd === 'add-sub') openAddModal(path);
                else if (cmd === 'edit') startEdit(path, node.name);
                else if (cmd === 'duplicate') duplicateNode(path);
                else if (cmd === 'multiply') openMultiplyModal(path, node.name);
                else if (cmd === 'delete') deleteNode(path);
              }
            "
          >
            <button type="button" class="btn-action">
              <PhosphorIcon name="dots-three" :size="18" />
            </button>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item command="add-sub">
                  <PhosphorIcon name="plus" :size="16" class="me-2" />
                  {{
                    t(
                      "dashboard:fieldObservations.partTemplates.editor.addChild"
                    )
                  }}
                </el-dropdown-item>
                <el-dropdown-item command="edit">
                  <PhosphorIcon name="pencil-simple" :size="16" class="me-2" />
                  {{ t("common:edit") }}
                </el-dropdown-item>
                <el-dropdown-item command="duplicate">
                  <PhosphorIcon name="copy" :size="16" class="me-2" />
                  {{
                    t(
                      "dashboard:fieldObservations.partTemplates.editor.duplicate"
                    )
                  }}
                </el-dropdown-item>
                <el-dropdown-item command="multiply">
                  <PhosphorIcon name="stack" :size="16" class="me-2" />
                  {{
                    t(
                      "dashboard:fieldObservations.partTemplates.editor.multiply"
                    )
                  }}
                </el-dropdown-item>
                <el-dropdown-item command="delete" divided class="text-danger">
                  <PhosphorIcon name="trash" :size="16" class="me-2" />
                  {{ t("common:delete") }}
                </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </div>
    </div>

    <!-- Add root button -->
    <button type="button" class="btn-add-root" @click="openAddModal(null)">
      <PhosphorIcon name="plus" :size="20" />
      <span>{{
        t("dashboard:fieldObservations.partTemplates.editor.addPart")
      }}</span>
    </button>

    <!-- Add Modal -->
    <el-dialog
      v-model="showAddModal"
      :title="
        addParentPath
          ? t('dashboard:fieldObservations.partTemplates.editor.addChild')
          : t('dashboard:fieldObservations.partTemplates.editor.addPart')
      "
      width="400px"
      :close-on-click-modal="false"
      @opened="addInputRef?.focus()"
    >
      <el-input
        ref="addInputRef"
        v-model="newNodeName"
        :placeholder="
          t('dashboard:fieldObservations.partTemplates.editor.namePlaceholder')
        "
        @keyup.enter="handleAdd"
      />
      <template #footer>
        <el-button @click="showAddModal = false">
          {{ t("common:cancel") }}
        </el-button>
        <el-button
          type="primary"
          :disabled="!newNodeName.trim()"
          @click="handleAdd"
        >
          {{ t("common:add") }}
        </el-button>
      </template>
    </el-dialog>

    <!-- Edit Modal -->
    <el-dialog
      v-model="showEditModal"
      :title="t('dashboard:fieldObservations.partTemplates.editor.editPart')"
      width="400px"
      :close-on-click-modal="false"
      @opened="editInputRef?.focus()"
    >
      <el-input
        ref="editInputRef"
        v-model="editingName"
        :placeholder="
          t('dashboard:fieldObservations.partTemplates.editor.namePlaceholder')
        "
        @keyup.enter="saveEdit"
      />
      <template #footer>
        <el-button @click="showEditModal = false">
          {{ t("common:cancel") }}
        </el-button>
        <el-button
          type="primary"
          :disabled="!editingName.trim()"
          @click="saveEdit"
        >
          {{ t("common:save") }}
        </el-button>
      </template>
    </el-dialog>

    <!-- Multiply Modal -->
    <el-dialog
      v-model="showMultiplyModal"
      :title="
        t('dashboard:fieldObservations.partTemplates.editor.multiplyTitle', {
          name: multiplySourceName,
        })
      "
      width="480px"
      :close-on-click-modal="false"
    >
      <div class="d-flex flex-column gap-3">
        <div
          v-for="(name, idx) in multiplyNames"
          :key="idx"
          class="d-flex align-items-center gap-2"
        >
          <span class="multiply-index">{{ idx + 1 }}</span>
          <el-input
            v-model="multiplyNames[idx]"
            :placeholder="
              t(
                'dashboard:fieldObservations.partTemplates.editor.namePlaceholder'
              )
            "
          />
        </div>

        <div class="d-flex gap-2">
          <button
            type="button"
            class="btn-multiply-action"
            @click="addMultiplyCopy"
          >
            <PhosphorIcon name="plus" :size="16" />
            {{
              t("dashboard:fieldObservations.partTemplates.editor.multiplyAdd")
            }}
          </button>
          <button
            v-if="multiplyNames.length > 2"
            type="button"
            class="btn-multiply-action btn-multiply-remove"
            @click="removeMultiplyCopy"
          >
            <PhosphorIcon name="minus" :size="16" />
            {{
              t(
                "dashboard:fieldObservations.partTemplates.editor.multiplyRemove"
              )
            }}
          </button>
        </div>
      </div>

      <template #footer>
        <el-button @click="showMultiplyModal = false">
          {{ t("common:cancel") }}
        </el-button>
        <el-button
          type="primary"
          :disabled="multiplyNames.filter((n) => n.trim()).length < 2"
          @click="handleMultiply"
        >
          {{ t("common:confirm") }}
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<style lang="scss" scoped>
.structure-editor {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  color: var(--bs-secondary-color);
  text-align: center;
  border: 2px dashed var(--bs-border-color);
  border-radius: var(--bs-border-radius-lg);

  .empty-icon {
    margin-bottom: 1rem;
    opacity: 0.5;
  }

  p {
    font-size: 14px;
    margin: 0;
  }
}

.tree {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--bs-border-color);
  border-radius: var(--bs-border-radius);
  overflow: hidden;
}

.tree-item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 12px;
  background-color: white;
  border-bottom: 1px solid var(--bs-border-color);

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: var(--bs-tertiary-bg);
  }
}

.btn-expand {
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 6px;
  background-color: transparent;
  color: var(--bs-secondary-color);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &:hover {
    background-color: var(--bs-secondary-bg);
  }
}

.expand-icon {
  transition: transform 0.2s ease;

  &.expanded {
    transform: rotate(90deg);
  }
}

.expand-spacer {
  width: 24px;
  flex-shrink: 0;
}

.node-name {
  flex: 1;
  font-size: 14px;
  font-weight: 500;
  color: var(--bs-body-color);
}

.node-actions {
  display: flex;
  gap: 4px;
}

.btn-action {
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 6px;
  background-color: transparent;
  color: var(--bs-secondary-color);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;

  &:hover {
    background-color: var(--bs-secondary-bg);
    color: var(--bs-body-color);
  }
}

.multiply-index {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: var(--bs-primary);
  color: white;
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.btn-multiply-action {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1px dashed var(--bs-border-color);
  border-radius: var(--bs-border-radius);
  background-color: transparent;
  color: var(--bs-secondary-color);
  font-size: 13px;
  cursor: pointer;

  &:hover {
    border-color: var(--bs-primary);
    color: var(--bs-primary);
  }

  &.btn-multiply-remove:hover {
    border-color: var(--bs-danger);
    color: var(--bs-danger);
  }
}

.btn-add-root {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 14px;
  border: 2px dashed var(--bs-border-color);
  border-radius: var(--bs-border-radius-lg);
  background-color: transparent;
  color: var(--bs-secondary-color);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: var(--bs-primary);
    color: var(--bs-primary);
  }
}
</style>
